import type { FighterId } from '@archon3d/core';

export interface FighterStatus {
  name: string;
  faction: string;
  hp: number;
  maxHp: number;
  /** Under Curse: it fights slowed, and the panel says so beside its name. */
  cursed?: boolean;
  /** Hit points the square's light adds to it in this fight, which the duel's intro names. */
  light?: number;
  /** In fire now: on burning ground or outside the closing ring, its health draining. */
  burning?: boolean;
}

/** What the DOM overlay shows about the duel that is playing in the scene. */
export interface BattleView {
  arenaId?: string;
  arenaName?: string;
  /** `intro` while fading into the arena and the count before the fight, `over` while the outcome plays out. */
  phase: 'intro' | 'fighting' | 'over';
  /**
   * The count before the fight: 3, 2, 1 in the intro, then 0 ("Fight!") for a moment once it
   * begins; `null` otherwise (x-aaa-gap#3).
   */
  countdown: number | null;
  human: FighterId | null;
  /** Every side a person at this device fights: both when two people meet with a controller each. */
  humans: FighterId[];
  attacker: FighterStatus;
  defender: FighterStatus;
  /** Seconds fought so far. */
  time: number;
  suddenDeath: boolean;
  /** When the arena starts to burn from its edges, in seconds of the fight: the panel warns of it. */
  suddenDeathAt: number;
  /** The clock ran out before anyone fell, and the fitter fighter won (`timeout` in the core). */
  timedOut: boolean;
  /** Seen from the local player; `null` until the fight is decided, and for a spectator. */
  outcome: 'won' | 'lost' | 'draw' | null;
  /** Who won once the fight is decided; `null` before that and when both fall. */
  winner: FighterId | null;
  /** A menu duel that does not touch the board. */
  practice: boolean;
  /**
   * A person is holding the button that gives the duel up: which, and how far through the hold.
   * Only the headset's panel said so; on a screen the duel ended without warning
   * (client-input:quality#2).
   */
  forfeitHold?: { share: number; button: string } | null;
  /** A game controller is connected, so the controls line names the pad's too. */
  pad?: boolean;
  /** The fighter whose person's own controller has gone, which stands still until it is back. */
  missingController?: string | null;
}

/** Bridges the Babylon arena and React: the stage publishes, the overlay subscribes. */
export class BattleFeed {
  private view: BattleView | null = null;
  private readonly listeners = new Set<() => void>();

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): BattleView | null {
    return this.view;
  }

  publish(view: BattleView | null): void {
    this.view = view;
    for (const listener of this.listeners) listener();
  }
}
